import { AppVisualState } from 'src/app/core/types/app-visual-state.type';
import { AppFilterType } from 'src/app/core/types/app-filter-type.type';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, PRIMARY_OUTLET, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { APP_MODULE_ACCENTS_ENABLED } from 'src/app/core/constants/app-visual-context.constant';
import { AppAccent } from 'src/app/core/types/app-accent.type';

@Injectable({ providedIn: 'root' })
export class AppVisualContextService {
  private readonly accentSubject = new BehaviorSubject<AppAccent>('default');
  readonly accent$ = this.accentSubject.asObservable();
  private initialized = false;

  constructor(private router: Router, private destroyRef: DestroyRef) {}

  get currentAccent(): AppAccent {
    return this.accentSubject.value;
  }

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.updateRouteAccent();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateRouteAccent());
  }

  resolveAccent(value: unknown): AppAccent {
    const accent = this.knownAccent(value);
    return APP_MODULE_ACCENTS_ENABLED[accent] ? accent : 'default';
  }

  classForAccent(value: unknown): string {
    return `app-accent-${this.resolveAccent(value)}`;
  }

  accentForInputs(accent?: AppAccent, fallback: AppAccent = this.currentAccent): AppAccent {
    return accent === undefined ? fallback : this.knownAccent(accent);
  }

  classForInputs(accent?: AppAccent, fallback?: AppAccent): string {
    return this.classForAccent(this.accentForInputs(accent, fallback));
  }

  accentForElement(element: Element | null): AppAccent {
    while (element) {
      const className = Array.from(element.classList).find(name => name.startsWith('app-accent-') &&
        Object.prototype.hasOwnProperty.call(APP_MODULE_ACCENTS_ENABLED, name.slice('app-accent-'.length)));
      if (className) return this.knownAccent(className.slice('app-accent-'.length));
      element = element.parentElement;
    }
    return this.currentAccent;
  }

  classForState(state?: AppVisualState): string {
    return state ? `app-state-${state}` : '';
  }

  classForFilter(type?: AppFilterType): string | undefined {
    return type ? `app-filter-${type}` : undefined;
  }

  private knownAccent(value: unknown): AppAccent {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(APP_MODULE_ACCENTS_ENABLED, value)
      ? value as AppAccent : 'default';
  }

  private updateRouteAccent(): void {
    let route: ActivatedRouteSnapshot | undefined = this.router.routerState.snapshot.root;
    let accent: AppAccent = 'default';
    while (route) {
      if (route.data['appAccent'] !== undefined) accent = this.knownAccent(route.data['appAccent']);
      route = route.children.find(child => child.outlet === PRIMARY_OUTLET);
    }
    if (this.currentAccent !== accent) this.accentSubject.next(accent);
  }
}
